import React, { useContext, useState } from "react";
import {
  Card,
  Row,
  Col,
  Avatar,
  Space,
  Typography,
  Tag,
  Badge,
  theme,
  Tooltip,
  Button,
  message,
} from "antd";
import {
  EnvironmentOutlined,
  TeamOutlined,
  CrownOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { LocationType } from "./types";
import { EmployeeDetails } from "../../../serviceoperation/entity/EmployeeOperationEntity";
import { getStatusStyle } from "../helpers/statusStyles";
import roleOptions from "../../../Util/constants";
import { syncStatusConfig } from "../helpers/syncStatusConfig";
import { EmployeeOperation } from "../../../serviceoperation/EmployeeOperation";
import { CompanyContext } from "../../../contexts/company-context";

const { Title, Text } = Typography;

interface Props {
  employee: EmployeeDetails;
  locations: LocationType[];
}

const EmployeeHeaderCard: React.FC<Props> = ({ employee, locations }) => {
  const { token } = theme.useToken();
  const { selectedCompany } = useContext(CompanyContext);
  const [isResyncLoading, setIsResyncLoading] = useState(false);

  const location =
    locations.find((l) => l.key === employee.organization?.branchId) || null;
  const department = employee.organization?.departmentName || null;

  const status =
    employee.invitePending && employee.isActive
      ? "Pending Invitation"
      : employee.isActive
      ? "Active"
      : "Terminated";
  const statusStyle = getStatusStyle(status);

  const roleLabel =
    roleOptions.find((r) => r.value === (employee.permissions?.[0] as number))
      ?.label || "N/A";

  const onResyncEmployee = (employeeId: string) => {
    setIsResyncLoading(true);
    const employeeOps = new EmployeeOperation();
    employeeOps
      .reSyncEmployeeAsync(selectedCompany?.corporationId as number, employeeId)
      .then(() => {
        setIsResyncLoading(false);
        message.info(
          `The re-sync request has been queued. Please check back shortly for updated status.`,
        );
      })
      .catch(() => {
        setIsResyncLoading(false);
        message.error("Failed to queue re-sync request. Please try again.");
      });
  };

  return (
    <Card
      style={{
        marginBottom: 24,
        borderRadius: 12,
        background: `linear-gradient(135deg, ${token.colorPrimaryBg} 0%, ${token.colorPrimaryBgHover} 100%)`,
        border: `1px solid ${token.colorPrimary}`,
        backgroundColor: token.colorBgContainer,
      }}
      bodyStyle={{ padding: 20 }}
    >
      <Row align="middle" gutter={24}>
        <Col>
          <div style={{ position: "relative", display: "inline-block" }}>
            <Avatar
              size={100}
              style={{
                border: `4px solid ${token.colorBgContainer}`,
                boxShadow: `0 4px 12px ${token.colorFillTertiary}`,
              }}
            >
              {employee.firstName?.[0]}
            </Avatar>

            <Badge
              status={
                employee.invitePending && employee.isActive
                  ? "warning"
                  : employee.isActive
                  ? "success"
                  : "error"
              }
              style={{ position: "absolute", bottom: 8, right: 8 }}
            />
          </div>
        </Col>

        <Col flex="auto">
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {employee.firstName} {employee.lastName}
              </Title>
              <Text type="secondary">{roleLabel}</Text>
            </div>

            <Space wrap align="center">
              {/* Employee Status */}
              <Tag
                style={{
                  background: statusStyle.bgColor,
                  borderColor: statusStyle.borderColor,
                  color: statusStyle.color,
                }}
              >
                {status}
              </Tag>

              {/* External Sync Status */}
              {employee.isSyncEnabled &&
                employee.syncStatus &&
                syncStatusConfig("")[employee.syncStatus] && (
                  <Tooltip
                    title={syncStatusConfig("")[employee.syncStatus].tooltip}
                  >
                    <Tag icon={syncStatusConfig("")[employee.syncStatus].icon}>
                      {syncStatusConfig("")[employee.syncStatus].label}
                    </Tag>
                  </Tooltip>
                )}

              {/* Retry Sync (Failed Only) */}
              {employee.isSyncEnabled && employee.syncStatus === "Failed" && (
                <Button
                  size="small"
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={() => onResyncEmployee(employee.employeeId)}
                  loading={isResyncLoading}
                >
                  Retry Sync
                </Button>
              )}

              {location && (
                <Tag
                  icon={<EnvironmentOutlined />}
                  style={{ borderColor: location.color }}
                >
                  {location.name}
                </Tag>
              )}

              {department && <Tag icon={<TeamOutlined />}>{department}</Tag>}

              {employee.managerName && (
                <Tag icon={<CrownOutlined />}>
                  Reports to {employee.managerName}
                </Tag>
              )}
            </Space>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

export default EmployeeHeaderCard;
